/**
 * pdf.js (v4 ESM) loader — ONE for every tool that needs pdf.js.
 *
 *   window.__pdfjsLoad(sources) → Promise<pdfjsLib>
 *     sources: [{ mjs: url, worker: url }, …]  tried in order, de-duplicated
 *
 * Why a file and not the inline `<script type="module">` text that
 * redact-pdf, sign-pdf and document-reader each carried (three copies of the
 * same loop): a parse failure inside injected TEXT is attributed to the PAGE,
 * so (a) the Sentry noise gate treats it as extension code
 * (sentry-noise-filter.js, isLateParseError — its premise is held by
 * NoInlineTextScriptInjectionTest), and (b) neither the ready nor the fail
 * event ever fires, so the tool's promise hangs with the spinner on. A plain
 * `import()` chain in a classic script (the pattern text-to-speech already
 * used) has neither problem: it needs no script element, and a failed import
 * REJECTS.
 *
 * Contract kept from the old loaders: sets window.__pdfjsLib / __pdfjsSrc,
 * sets GlobalWorkerOptions.workerSrc, dispatches `pdfjsReady` / `pdfjsFail`
 * on window (document-reader listens for them). Difference: a total failure
 * is NOT cached — the next call retries, so a transient network blip does
 * not brick the page until reload.
 */
(function () {
    'use strict';
    var inflight = null;

    function load(sources, opts) {
        if (window.__pdfjsLib) return Promise.resolve(window.__pdfjsLib);
        if (inflight) return inflight;
        var importer = (opts && opts.importer) || function (u) { return import(u); };
        var list = [], seen = {};
        (sources || []).forEach(function (s) {
            if (!s || !s.mjs || seen[s.mjs]) return;
            seen[s.mjs] = 1;
            list.push(s);
        });
        inflight = new Promise(function (resolve, reject) {
            var i = 0;
            (function next(lastErr) {
                if (window.__pdfjsLib) return resolve(window.__pdfjsLib);
                if (i >= list.length) {
                    var err = new Error('pdfjs_load_fail');
                    err.cause = lastErr;
                    return reject(err);
                }
                var src = list[i++];
                Promise.resolve().then(function () { return importer(src.mjs); }).then(function (m) {
                    if (m && m.GlobalWorkerOptions) m.GlobalWorkerOptions.workerSrc = src.worker;
                    window.__pdfjsLib = m;
                    window.__pdfjsSrc = src.mjs;
                    resolve(m);
                }, next);
            })(null);
        });
        inflight.then(function () {
            try { window.dispatchEvent(new Event('pdfjsReady')); } catch (_) {}
        }, function () {
            inflight = null; // do not cache the failure — allow a retry
            try { window.dispatchEvent(new Event('pdfjsFail')); } catch (_) {}
        });
        return inflight;
    }

    window.__pdfjsLoad = load;
})();
